import { useState } from 'react'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import Divider from '@mui/material/Divider'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import CheckIcon from '@mui/icons-material/Check'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import Popover from '@mui/material/Popover'
import Typography from '@mui/material/Typography'
import { useQuery } from '@tanstack/react-query'

import { getWallet } from '../api/portal'
import { can } from '../auth/session'
import { usePortal } from '../auth/sessionContext'
import { formatIrr } from '../format'
import { presentation } from '../portalMode'

/** Two letters of the account name, as an identity mark rather than a picture. */
function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean)
  return words.slice(0, 2).map((word) => word[0]).join('')
}

/**
 * Who you are acting as, and — for a service customer — what you have to spend.
 *
 * This replaced a native `<select>` sitting in the app bar. Two things were
 * wrong with it. It looked like a form control in a place nothing else was
 * editable, and it appeared even when there was exactly one account to choose,
 * which is most customers: a dropdown with one option is a control that does
 * nothing, and a customer who opens it learns only that they were right the
 * first time.
 *
 * So: one membership renders as identity, with no affordance and nothing to
 * open. Several render as a switcher. The balance rides along because the
 * product owner found it too well hidden — a service customer should not have
 * to navigate to the wallet to learn whether their next call will succeed.
 */
export function AccountCard() {
  const { session, membership, selectCustomer } = usePortal()
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const many = session.memberships.length > 1
  const view = presentation(membership)

  const wallet = useQuery({
    queryKey: ['client', 'wallet', membership.customer.id],
    queryFn: ({ signal }) => getWallet(membership.customer.id, signal),
    enabled: view.showsBalanceInChrome && can(membership, 'view_wallet'),
    // Money the customer is about to spend. A stale figure here is worse than
    // a missing one, so it is refetched when the tab comes back rather than
    // served from a cache of unknown age.
    staleTime: 15_000,
    refetchOnWindowFocus: true,
  })
  // Gated on the policy, not on the query. A disabled query still reads what
  // another component has already put in the shared cache, so `enabled` alone
  // let the balance appear in the professional sidebar the moment any page had
  // fetched the wallet.
  const balance =
    view.showsBalanceInChrome && wallet.data
      ? formatIrr(wallet.data.available_balance_irr)
      : null

  const identity = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0, width: '100%' }}>
      <Avatar
        variant="rounded"
        sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontSize: 14, fontWeight: 700 }}
      >
        {initials(membership.customer.name)}
      </Avatar>
      <Box sx={{ minWidth: 0, textAlign: 'start', flexGrow: 1 }}>
        <Typography variant="body2" noWrap sx={{ fontWeight: 600, lineHeight: 1.4 }}>
          {membership.customer.name}
        </Typography>
        {balance ? (
          <Typography variant="caption" color="text.secondary" noWrap component="div">
            موجودی: {balance}
          </Typography>
        ) : null}
      </Box>
      {many ? <ExpandMoreIcon fontSize="small" sx={{ color: 'text.secondary' }} /> : null}
    </Box>
  )

  return (
    <Box sx={{ px: 1.5, pt: 1.5 }}>
      {many ? (
        <ButtonBase
          onClick={(event) => setAnchor(event.currentTarget)}
          aria-haspopup="menu"
          aria-label="تغییر حساب"
          sx={{
            width: '100%',
            p: 1,
            borderRadius: 1.5,
            border: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          {identity}
        </ButtonBase>
      ) : (
        // No affordance, because there is nothing to open. The account is a
        // fact here, not a choice.
        <Box
          sx={{
            p: 1,
            borderRadius: 1.5,
            border: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          {identity}
        </Box>
      )}

      <Popover
        open={anchor !== null}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { width: 280, mt: 0.5 } } }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 2, pt: 1.5 }}>
          حساب‌های من
        </Typography>
        <List disablePadding sx={{ py: 1 }}>
          {session.memberships.map((row) => {
            const current = row.customer.id === membership.customer.id
            return (
              <ListItemButton
                key={row.customer.id}
                selected={current}
                onClick={() => {
                  selectCustomer(row.customer.id)
                  setAnchor(null)
                }}
                sx={{ gap: 1.25, alignItems: 'flex-start' }}
              >
                <CheckIcon
                  fontSize="small"
                  sx={{ mt: 0.25, visibility: current ? 'visible' : 'hidden' }}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: current ? 600 : 400 }}>
                    {row.customer.name}
                  </Typography>
                  {current && balance ? (
                    <Typography variant="caption" color="text.secondary">
                      {balance}
                    </Typography>
                  ) : null}
                </Box>
              </ListItemButton>
            )
          })}
        </List>
        <Divider />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', p: 2 }}>
          دسترسی به حساب‌ها را گردانندهٔ سامانه تعیین می‌کند.
        </Typography>
      </Popover>
    </Box>
  )
}
